/*
 * Inline images.
 *
 * An author writes the standard markdown image, and nothing more exotic:
 *
 *     ![Alt text](file-name.png "Optional title")
 *
 * The destination is a FILE NAME - not a URL, not a GUID - resolved against the
 * images attached to the article being rendered (kb.ArticleImage, keyed
 * (ArticleId, FileName)). An absolute http/https destination passes through
 * untouched, because pointing at an external image is still allowed.
 *
 * Why standard markdown here, when article links use a [[kb:...]] token
 * ---------------------------------------------------------------------
 * A link token buys something: an id survives a slug rename, and the reference
 * is discoverable, so "what points at this?" can be answered before a delete.
 * An image reference buys none of that. The file name IS the key - renaming it
 * breaks the article by definition - and the image cannot exist without the
 * article it hangs off. So there is nothing for a token to indirect through, and
 * inventing one would only stop every markdown editor, preview pane and
 * paste-a-screenshot habit in the world from working.
 *
 * These are two separate mechanisms that happen to look alike in the source
 * text. What they share is the low-level markdown handling from articleLinks.ts -
 * code masking, text escaping, href encoding - and nothing above that; do not
 * merge the resolvers.
 *
 * What lives here, and why it is duplicated from the database
 * ----------------------------------------------------------
 * The file-name rules below are a second implementation of
 * CK_ArticleImage_FileName, exactly as slug.ts is a second implementation of the
 * four slug CHECKs. The database stays the authority - a collision is caught by
 * the primary key, not by this file - but a rule the browser cannot evaluate is
 * a rule the author only learns about after uploading 4 MB, and a rule the
 * server spells differently from the database is a 500 where a field error
 * belonged.
 *
 * The size cap and the format allow-list are here for the same reason, and are
 * exported as constants rather than written out at each end: the browser's
 * pre-flight check, the server's rejection and the CHECK constraint have to be
 * the same number, or the difference between them is a bug that only appears on
 * a 5.1 MB file.
 *
 * Everything here is pure string and byte handling - no I/O, no DOM, no SQL -
 * shared by the server (which validates uploads and serves bytes) and the
 * browser (which pastes, previews and renders).
 */
import { encodeMarkdownHref, escapeMarkdownText, maskMarkdownCode } from './articleLinks.js';
import { toSlug } from './slug.js';
// ---------------------------------------------------------------------------
// The rules the database also enforces
// ---------------------------------------------------------------------------
/**
 * The four raster formats an image may be stored as, matching
 * CK_ArticleImage_ContentType.
 *
 * SVG IS ABSENT ON PURPOSE AND MUST STAY ABSENT. An SVG document can carry
 * <script> and event handlers, so serving one inline from our own origin is
 * stored XSS with extra steps. "Sanitise it instead" turns a sanitiser nobody
 * maintains into a security boundary, and PNG or WEBP renders a diagram
 * perfectly well. Adding 'image/svg+xml' to this array would be accepted by
 * neither the magic-byte sniffer below nor the CHECK constraint, and that is the
 * intended amount of friction.
 */
export const ARTICLE_IMAGE_CONTENT_TYPES = [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
];
/**
 * 5 MB, matching CK_ArticleImage_ByteLength. The bytes travel through the same
 * connection pool as every other query, so this is a real limit and not a
 * formality: it is what keeps one screenshot from occupying a pool connection
 * for the duration of a large upload.
 */
export const ARTICLE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
/**
 * At most 20 images on one article, matching kb.TR_ArticleImage_MaxPerArticle.
 *
 * The owner's number (2026-09-21). The bytes live in the database and the editor
 * downloads every one of them when an article is opened - there are no
 * thumbnails, by decision - so the count, not only the size, is what bounds one
 * article's weight: twenty at the 5 MB maximum is 100 MB, and that is the most an
 * editor open should ever be asked to fetch. Counted before a NEW name is written,
 * in the browser (a refusal before the upload starts), on the server (the upload
 * endpoint and both ingest operations) and in the database (the trigger), which
 * is the arrangement the 5 MB cap already has. Replacing an existing image never
 * counts against it - the article is not gaining a picture.
 */
export const ARTICLE_IMAGE_MAX_PER_ARTICLE = 20;
/** kb.ArticleImage.FileName is NVARCHAR(200). */
export const ARTICLE_IMAGE_FILE_NAME_MAX_LENGTH = 200;
/**
 * Mirrors CK_ArticleImage_FileName, term for term:
 *
 *     first character   a-z or 0-9      (so no leading dot and no leading hyphen)
 *     stem              [a-z0-9-]*
 *     exactly one dot   neither character class contains '.'
 *     extension         2 to 4 characters, the last one not a hyphen
 *
 * So `connector-overview.png` passes, and `Connector Overview.PNG`,
 * `sub/dir/x.png`, `x.png.png`, `.png`, `x.` and `-x.png` do not.
 *
 * Deliberately no case-insensitive flag. The constraint is enforced under
 * Latin1_General_100_BIN2 precisely so that it means code points rather than
 * whatever the collation thinks sorts near 'a' - see the header of
 * Tables/kb.ArticleImage.sql for the 25-of-26-uppercase-letters measurement that
 * made BIN2 necessary. A pattern that accepted `Foo.PNG` here would hand the
 * author a name the INSERT then refuses.
 */
export const ARTICLE_IMAGE_FILE_NAME_PATTERN = /^[a-z0-9][a-z0-9-]*\.[a-z0-9-]{1,3}[a-z0-9]$/;
/** The prefix a generated name for a pasted image gets. */
export const PASTED_IMAGE_NAME_PREFIX = 'pasted-';
/** Ready for an <input type="file"> accept attribute, so it cannot drift. */
export const ARTICLE_IMAGE_ACCEPT_ATTRIBUTE = ARTICLE_IMAGE_CONTENT_TYPES.join(',');
/**
 * The extension each stored type is written with: one canonical extension per
 * type, because this is the name a generator produces. `.jpeg` is accepted on
 * the way in (see `articleImageContentTypeForExtension`) and normalised to
 * `.jpg` on the way out.
 */
const CANONICAL_EXTENSIONS = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/webp': 'webp',
};
const EXTENSION_CONTENT_TYPES = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
};
export function isArticleImageContentType(value) {
    return ARTICLE_IMAGE_CONTENT_TYPES.includes(value);
}
export function isValidArticleImageFileName(fileName) {
    return (typeof fileName === 'string' &&
        fileName.length > 0 &&
        fileName.length <= ARTICLE_IMAGE_FILE_NAME_MAX_LENGTH &&
        ARTICLE_IMAGE_FILE_NAME_PATTERN.test(fileName));
}
/** The message shown next to a rejected file-name field. */
export function describeArticleImageFileNameRequirement() {
    return ('Lowercase letters a-z, digits and hyphens, with one dot and a 2-4 character ' +
        'extension - for example connector-overview.png.');
}
/** The message shown next to an upload that is too big. */
export function describeArticleImageSizeLimit() {
    return `Images must be ${formatByteSize(ARTICLE_IMAGE_MAX_BYTES)} or smaller.`;
}
/** The message shown when an article already carries as many images as it may. */
export function describeArticleImageCountLimit() {
    return (`An article can carry at most ${ARTICLE_IMAGE_MAX_PER_ARTICLE} images. ` +
        'Delete one before attaching another, or replace an existing one.');
}
export function extensionForArticleImageContentType(contentType) {
    return CANONICAL_EXTENSIONS[contentType];
}
/**
 * The type an extension implies, or null. Used only to make sense of a name the
 * client supplied - never to decide what is stored, which comes from the bytes.
 */
export function articleImageContentTypeForExtension(extension) {
    const normalised = (extension ?? '')
        .trim()
        .toLowerCase()
        .replace(/^\./, '');
    return EXTENSION_CONTENT_TYPES[normalised] ?? null;
}
/** The extension of a file name, without the dot, lowercased. '' when there is none. */
export function articleImageExtension(fileName) {
    const dot = (fileName ?? '').lastIndexOf('.');
    return dot === -1 ? '' : fileName.slice(dot + 1).toLowerCase();
}
// ---------------------------------------------------------------------------
// The upload wire format
//
// The one place that decides HOW an upload is addressed and labelled, because
// getting it wrong is not a degraded feature - it is every upload failing. The
// first version of this feature shipped a browser that POSTed multipart/form-data
// to a server that mounts express.raw and rejects multipart BY NAME, so the two
// ends never met. Nothing caught it: both sides had passing unit tests, and
// neither test crossed the boundary.
//
// So the format lives here, next to the constants that were already shared for
// exactly this reason, and both the client that builds a request and the test that
// asserts the server accepts one read it from here.
//
//     POST /api/admin/articles/{id}/images?fileName=shot.png&replace=false
//     Content-Type: image/png
//     <the raw bytes>
//
// The body is the image, so the fields have nowhere to go but the query string.
// ---------------------------------------------------------------------------
/** The declared type for a payload the client cannot vouch for. */
export const ARTICLE_IMAGE_UNDECLARED_CONTENT_TYPE = 'application/octet-stream';
/**
 * The query string of an upload, without the leading '?'.
 *
 * `replace` is always written, even though the schema defaults it to false: an
 * explicit value cannot be misread, and this is the flag whose silent default
 * loses an author the upload they thought they had confirmed. `fileName` is
 * written only when there is one - an empty parameter would be a name the server
 * has to decide how to ignore.
 *
 * The names are `fileName` and `replace` because `articleImageUploadSchema` reads
 * exactly those two keys off `request.query`. That schema is the counterpart of
 * this function and the two must be changed together.
 */
export function articleImageUploadQuery(fields) {
    const query = new URLSearchParams();
    if (fields.fileName)
        query.set('fileName', fields.fileName);
    query.set('replace', fields.replace === true ? 'true' : 'false');
    return query.toString();
}
/**
 * The Content-Type to declare for an upload, given whatever type the source blob
 * claims for itself.
 *
 * DECLARE A TYPE ONLY WHEN IT CAN BE RIGHT. The server compares what the client
 * declares against what it sniffs from the magic bytes and REJECTS a disagreement
 * rather than correcting it, so a near-miss label is worse than no label at all: a
 * blob calling itself `image/jpg` - which a scanner or a desktop file picker will
 * produce - would be refused on the label alone though its bytes are a perfectly
 * storable JPEG. Anything not on the accepted list therefore travels as
 * `application/octet-stream`, which the server documents as "the client asserts
 * nothing" and which leaves the sniffed type to decide alone.
 *
 * NOTE WHAT THIS COSTS, so it is not a surprise: an SVG loses its declared type
 * here too, and with it the server's specific "SVG images are not accepted"
 * message - the sniffer then refuses it as a generic unsupported format. That is
 * acceptable only because the browser never gets that far: the editor runs
 * `inspectArticleImageBytes` over the file's leading bytes BEFORE uploading, so an
 * SVG is refused locally with its own message and no request is made. The server's
 * by-name SVG rejection is for clients that are not this one, and it stays.
 */
export function declaredArticleImageUploadType(blobType) {
    const declared = (blobType ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
    return isArticleImageContentType(declared)
        ? declared
        : ARTICLE_IMAGE_UNDECLARED_CONTENT_TYPE;
}
/**
 * Identifies an upload by its MAGIC BYTES, and by nothing else.
 *
 * The declared Content-Type and the file extension are both client-controlled -
 * or, far more often, simply wrong, because somebody renamed a screenshot. A
 * declared type that disagrees with the bytes is therefore a REJECTION and not
 * something to correct: trusting either one is how a .png that is really an HTML
 * document ends up being served from our own origin.
 *
 * The signatures, and why each is enough:
 *
 *   PNG   89 50 4E 47 0D 0A 1A 0A   the full 8-byte signature, not just 'PNG'
 *   JPEG  FF D8 FF                  SOI, plus the first marker byte
 *   GIF   'GIF87a' or 'GIF89a'
 *   WEBP  'RIFF' ???? 'WEBP'        RIFF container, WEBP form type at offset 8
 *
 * This is deliberately NOT a decoder. It proves the file claims to be one of
 * four raster formats; it does not prove the pixels are valid. That is the right
 * boundary: a corrupt PNG is the author's problem and renders as a broken image,
 * whereas a script-bearing document mislabelled as a PNG is our problem, and the
 * signature check is what stops it.
 */
export function sniffArticleImageContentType(bytes) {
    const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    if (startsWith(view, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]))
        return 'image/png';
    if (startsWith(view, [0xff, 0xd8, 0xff]))
        return 'image/jpeg';
    if (startsWith(view, ascii('GIF87a')) || startsWith(view, ascii('GIF89a')))
        return 'image/gif';
    // 'RIFF', then four length bytes we do not care about, then 'WEBP'.
    if (startsWith(view, ascii('RIFF')) && matchesAt(view, 8, ascii('WEBP')))
        return 'image/webp';
    return null;
}
/**
 * The whole upload gate on the bytes, in one call, so the browser can run the
 * identical check before spending a minute uploading something that was never
 * going to be accepted. Advisory in the browser, authoritative on the server -
 * the browser is not trusted, it is merely being helpful.
 */
export function inspectArticleImageBytes(bytes) {
    const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const byteLength = view.byteLength;
    if (byteLength === 0) {
        return {
            ok: false,
            contentType: null,
            byteLength,
            rejection: 'empty',
            message: 'That file is empty.',
        };
    }
    if (byteLength > ARTICLE_IMAGE_MAX_BYTES) {
        return {
            ok: false,
            contentType: null,
            byteLength,
            rejection: 'too-large',
            message: `That image is ${formatByteSize(byteLength)}. ${describeArticleImageSizeLimit()}`,
        };
    }
    const contentType = sniffArticleImageContentType(view);
    if (contentType === null) {
        return {
            ok: false,
            contentType: null,
            byteLength,
            rejection: 'unsupported-format',
            // SVG is named explicitly: it is the format authors reach for, and the
            // answer is always no, so saying so here saves a support round trip.
            message: 'That is not a PNG, JPEG, GIF or WEBP image. SVG is not accepted, because an SVG ' +
                'can carry scripts.',
        };
    }
    return { ok: true, contentType, byteLength, rejection: null, message: null };
}
/**
 * Human-readable size, for a validation message or the editor's image list.
 * Powers of 1024 with the decimal prefixes, which is the convention every file
 * manager the author has ever used follows.
 */
export function formatByteSize(bytes) {
    if (!Number.isFinite(bytes) || bytes <= 0)
        return '0 bytes';
    if (bytes < 1024) {
        const whole = Math.round(bytes);
        return `${whole} byte${whole === 1 ? '' : 's'}`;
    }
    const kilobytes = bytes / 1024;
    if (kilobytes < 1024)
        return `${trimDecimal(kilobytes)} kB`;
    return `${trimDecimal(kilobytes / 1024)} MB`;
}
// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------
/**
 * Turns whatever the client called the file into a name the CHECK constraint
 * accepts, keeping as much of the original as survives.
 *
 * The stem goes through `toSlug`, and that is not a coincidence: a file name and
 * a slug have the same character class, so a screenshot called
 * "Skaermbillede 2026-08-25 (1).PNG" becomes "skaermbillede-2026-08-25-1.png"
 * instead of being thrown away for a generated name the author does not
 * recognise. The transliteration table in slug.ts is what keeps the Danish
 * letters readable rather than turning them into hyphens.
 *
 * The extension always comes from `contentType`, which the caller sniffed from
 * the bytes - never from the name. A .jpg holding a PNG is stored as .png,
 * because the extension the author reads in the markdown should be the truth
 * about the file.
 *
 * Returns null when nothing usable is left, which is the caller's cue to
 * generate a `pasted-<n>` name instead.
 */
export function toArticleImageFileName(input, contentType) {
    const extension = extensionForArticleImageContentType(contentType);
    const raw = (input ?? '').trim();
    // Drop any directory part a browser may have sent, then the extension: it is
    // replaced by the sniffed one, and a leftover dot would break the "exactly one
    // dot" rule.
    const base = raw.split(/[\\/]/).pop() ?? '';
    const dot = base.lastIndexOf('.');
    const stem = toSlug(dot > 0 ? base.slice(0, dot) : base, ARTICLE_IMAGE_FILE_NAME_MAX_LENGTH - extension.length - 1);
    if (stem.length === 0)
        return null;
    const fileName = `${stem}.${extension}`;
    // Belt and braces: if the composed name somehow fails the mirror of the CHECK,
    // say so by returning null rather than handing the database a name it refuses.
    return isValidArticleImageFileName(fileName) ? fileName : null;
}
/**
 * The next free `pasted-<n>.<ext>` name for an article.
 *
 * A pasted screenshot has no name at all, and the author SEES this one in the
 * markdown the moment it is inserted - so it is a readable counter rather than a
 * GUID or a content hash. "pasted-3.png" tells them which paste it was; a
 * content-addressed name tells them nothing and makes the markdown unreadable.
 *
 * `n` is the lowest free integer, and a number counts as taken when ANY
 * extension uses it: once pasted-1.png exists, the next paste is pasted-2 even
 * if it is a JPEG. Reusing the number with a different extension would give one
 * article two names a single character apart, which is exactly the sort of thing
 * an author then mistypes in the markdown.
 */
export function nextPastedImageFileName(taken, contentType) {
    const extension = extensionForArticleImageContentType(contentType);
    const used = new Set();
    for (const name of taken) {
        const match = /^pasted-(\d{1,6})\.[a-z0-9-]{2,4}$/.exec((name ?? '').trim().toLowerCase());
        if (match)
            used.add(Number.parseInt(match[1] ?? '0', 10));
    }
    let n = 1;
    while (used.has(n))
        n += 1;
    return `${PASTED_IMAGE_NAME_PREFIX}${n}.${extension}`;
}
// ---------------------------------------------------------------------------
// The markdown grammar
// ---------------------------------------------------------------------------
/** Cheap test before doing any work, and what an editor toolbar inserts. */
export const ARTICLE_IMAGE_MARKDOWN_PREFIX = '![';
/**
 * Where the caret goes after inserting `![](name.png)`: just inside the alt-text
 * brackets, so the author can type a description immediately. `'!['.length`,
 * named rather than written as a 2 at the call site.
 */
export const ARTICLE_IMAGE_ALT_CARET_OFFSET = ARTICLE_IMAGE_MARKDOWN_PREFIX.length;
/**
 * Matches `![alt](destination "title")`.
 *
 * Bounded on purpose, in the same spirit as the article-link pattern - and every
 * boundary below is drawn where it is because the obvious version broke
 * something real:
 *
 *   - the alt part accepts `\]` but not a bare `]`, so a reference cannot swallow
 *     the bracket that ends it while an author can still put a bracket in a
 *     caption. Excluding it outright drops the whole image silently, which is a
 *     worse failure than any rendering mistake it could have made.
 *   - the bare destination excludes whitespace and tolerates ONE level of
 *     balanced parentheses. The parentheses are not a nicety: without them
 *     `javascript:alert(1)` is not recognised as a destination at all, so it is
 *     never classified unsafe and survives into the HTML for the renderer to
 *     interpret. A destination that genuinely needs a space must use the `<...>`
 *     form, which CommonMark also allows - though a legal FILE NAME never can.
 *   - every part excludes newlines. CommonMark tolerates a line break inside the
 *     destination; we do not, because the alternative is a pattern that can
 *     match across a blank line and fuse two unrelated paragraphs into one
 *     malformed image.
 *   - the title accepts `\"`, so a quoted phrase inside a title does not
 *     terminate it early.
 *
 * Deeper nesting, and an unbalanced parenthesis, produce no match at all. That
 * is the safe direction only because nothing is then rewritten AND nothing is
 * claimed to be an image; the sanitiser at the end of the render pipeline covers
 * the remainder, which is why Markdown.tsx sanitises the output instead of
 * trusting this.
 *
 * Malformed input is left alone rather than half-parsed: `![alt](` produces no
 * reference at all, so the renderer emits it verbatim and the author sees their
 * own typo instead of a mangled rewrite.
 */
const IMAGE_PATTERN = /!\[((?:[^\]\\\r\n]|\\.)*)\]\([ \t]*(?:<([^<>\r\n]*)>|((?:[^\s()]|\([^\s()]*\))*))(?:[ \t]+(?:"((?:[^"\\\r\n]|\\.)*)"|'((?:[^'\\\r\n]|\\.)*)'|\(((?:[^()\\\r\n]|\\.)*)\)))?[ \t]*\)/g;
/**
 * Finds every image reference in a markdown document.
 *
 * References inside fenced blocks, indented blocks and inline code are skipped,
 * for the same reason article-link tokens are: an article explaining this very
 * syntax has to be able to show `![alt](file.png)` without it becoming an image.
 * The masking helper is shared with articleLinks.ts rather than reimplemented -
 * two scanners disagreeing about what counts as code would be a subtle and
 * lasting bug.
 *
 * Duplicated references to the same file are all returned, each with its own
 * offset, because rewriting replaces occurrences by position.
 */
export function parseArticleImageReferences(markdown) {
    if (!markdown || !markdown.includes(ARTICLE_IMAGE_MARKDOWN_PREFIX))
        return [];
    const masked = maskMarkdownCode(markdown);
    const references = [];
    IMAGE_PATTERN.lastIndex = 0;
    let match;
    while ((match = IMAGE_PATTERN.exec(masked)) !== null) {
        const index = match.index;
        // A backslash-escaped bang is a literal "!" followed by an ordinary link,
        // not an image. The run is counted, so `\\![x](y)` is an escaped backslash
        // followed by a real image.
        if (isEscaped(masked, index))
            continue;
        const rawTarget = (match[2] ?? match[3] ?? '').trim();
        const rawTitle = match[4] ?? match[5] ?? match[6];
        const reference = {
            // Read from the original, not the masked copy, so the recorded text is
            // byte-identical to what the author wrote.
            markdown: markdown.slice(index, index + match[0].length),
            index,
            alt: unescapeMarkdown(match[1] ?? ''),
            target: rawTarget,
            ...classifyArticleImageTarget(rawTarget),
        };
        if (rawTitle !== undefined)
            reference.title = unescapeMarkdown(rawTitle);
        references.push(reference);
    }
    return references;
}
/**
 * Decides what a destination is, and there are three answers rather than two.
 *
 *   relative   a bare file name. Rewritten to the image endpoint of the article
 *              being rendered.
 *   absolute   http:// or https://. Passes through completely untouched - an
 *              author may point at an image hosted elsewhere.
 *   unsafe     everything else, and it MUST NOT become a src.
 *
 * The third case is the one that matters. A `data:` or `javascript:` URL in an
 * image src is an injection vector: `javascript:` still executes on some legacy
 * and SVG-ish paths, and a `data:` src is an arbitrary payload delivered inside
 * our own origin's document, which defeats the entire point of refusing SVG
 * uploads. A protocol-relative `//host/x.png` silently inherits the page scheme
 * and loads from a third party, which is a privacy leak the author almost
 * certainly did not mean to write.
 *
 * A destination containing a slash is unsafe as well, not merely unresolvable. A
 * legal FileName cannot contain one, so `../../something` is either a mistake or
 * an attempt at traversal, and neither should be turned into a path we then ask
 * a server for.
 *
 * The file name is lowercased. That is not a silent correction: FileName is
 * stored under the database's default CI collation, so `WHERE FileName =
 * 'Connector.PNG'` already matches the stored `connector.png`. Lowercasing here
 * only makes the audit's set comparisons agree with the lookup the server will
 * actually perform. Whether a name could ever be STORED is a separate question,
 * answered by `isValidArticleImageFileName`.
 */
export function classifyArticleImageTarget(target) {
    const value = (target ?? '').trim();
    if (value.length === 0)
        return { kind: 'unsafe', unsafeReason: 'empty' };
    if (/^https?:\/\//i.test(value))
        return { kind: 'absolute' };
    if (value.startsWith('//'))
        return { kind: 'unsafe', unsafeReason: 'protocol-relative' };
    // Any other scheme: data:, javascript:, vbscript:, file:, mailto: ...
    if (/^[a-z][a-z0-9+.-]*:/i.test(value))
        return { kind: 'unsafe', unsafeReason: 'scheme' };
    if (/[\\/]/.test(value))
        return { kind: 'unsafe', unsafeReason: 'path' };
    // A query or fragment cannot be part of a file name either, and appending one
    // to the endpoint URL would let an author reach a different route.
    if (/[?#]/.test(value))
        return { kind: 'unsafe', unsafeReason: 'path' };
    return { kind: 'relative', fileName: value.toLowerCase() };
}
/** Distinct relative file names, in first-appearance order, for batched lookup. */
export function collectArticleImageFileNames(references) {
    const names = new Set();
    for (const reference of references) {
        if (reference.kind === 'relative' && reference.fileName)
            names.add(reference.fileName);
    }
    return [...names];
}
/**
 * How many times an article's text references one attached image. This is what
 * makes "you are deleting an image the article still uses" possible.
 */
export function countArticleImageReferences(markdown, fileName) {
    const wanted = (fileName ?? '').trim().toLowerCase();
    if (wanted.length === 0)
        return 0;
    return parseArticleImageReferences(markdown).filter((reference) => reference.kind === 'relative' && reference.fileName === wanted).length;
}
/**
 * Everything the editor needs in order to tell an author what is wrong with
 * their images, computed from the text and the list of attachments and nothing
 * else.
 *
 * Why the editor reports these rather than the renderer: a missing attachment
 * renders as a broken image, which a reader can see and do nothing about. The
 * author is the only person who can fix it, so the warning belongs where they
 * are working. `missingAlt` is here for the same reason - alt text is an
 * accessibility requirement, and an empty one is easy to leave behind when the
 * paste flow puts the caret in the brackets and the author types the body
 * instead.
 */
export function auditArticleImages(markdown, attachedFileNames) {
    const references = parseArticleImageReferences(markdown);
    const attached = new Set([...attachedFileNames].map((name) => (name ?? '').toLowerCase()));
    const missing = [];
    const seenMissing = new Set();
    const referenced = new Set();
    const malformed = [];
    const unsafe = [];
    const missingAlt = [];
    for (const reference of references) {
        if (reference.alt.trim().length === 0)
            missingAlt.push(reference);
        if (reference.kind === 'unsafe') {
            unsafe.push(reference);
            continue;
        }
        if (reference.kind !== 'relative' || !reference.fileName)
            continue;
        const fileName = reference.fileName;
        referenced.add(fileName);
        if (!isValidArticleImageFileName(fileName)) {
            malformed.push(reference);
            continue;
        }
        if (!attached.has(fileName) && !seenMissing.has(fileName)) {
            seenMissing.add(fileName);
            missing.push(fileName);
        }
    }
    const unused = [...attached].filter((fileName) => !referenced.has(fileName));
    return { references, missing, unused, missingAlt, malformed, unsafe };
}
/**
 * Builds the markdown for an image, for the paste handler and the editor's
 * insert button.
 *
 * The alt text and title are escaped so a description containing a bracket or a
 * quote cannot terminate the reference early - the same job `sanitiseTokenText`
 * does for an article link, done by escaping rather than stripping because an
 * image caption is prose, and losing a character from it is more surprising than
 * seeing a backslash in the source.
 */
export function buildArticleImageMarkdown(options) {
    const alt = escapeAltText(options.alt ?? '');
    const title = options.title ? ` "${options.title.replace(/([\\"])/g, '\\$1')}"` : '';
    return `![${alt}](${encodeMarkdownHref(options.fileName)}${title})`;
}
/**
 * Rewrites relative image destinations to the URL that actually serves them -
 * the public endpoint on the public site, the admin endpoint in the editor
 * preview - and leaves everything else alone.
 *
 * Only the DESTINATION is replaced. The alt text and title are re-emitted
 * byte for byte as the author wrote them, escapes and quote style intact, so
 * running this over a document cannot alter a caption.
 *
 * Absolute destinations pass through untouched. Unsafe ones are replaced, never
 * rewritten - see `classifyArticleImageTarget`.
 *
 * A relative name that is not attached is rewritten anyway, on purpose: the
 * endpoint answers 404, the browser shows its broken-image indication with the
 * alt text beside it, and the author sees that something is wrong. Silently
 * dropping the image would leave a blank gap, which is the one outcome nobody
 * can act on. `auditArticleImages` is what turns that into a sentence the author
 * can read.
 *
 * Replacement runs back to front so earlier offsets stay valid, exactly as
 * `renderArticleLinks` does.
 */
export function rewriteArticleImageSources(markdown, options) {
    const references = parseArticleImageReferences(markdown);
    if (references.length === 0)
        return markdown;
    let output = markdown;
    for (const reference of [...references].sort((a, b) => b.index - a.index)) {
        // Guard against a reference list that has drifted from the text.
        if (output.slice(reference.index, reference.index + reference.markdown.length) !==
            reference.markdown) {
            continue;
        }
        let replacement = null;
        if (reference.kind === 'relative' && reference.fileName) {
            const src = encodeMarkdownHref(options.buildSrc(reference.fileName));
            replacement = replaceDestination(reference.markdown, src);
        }
        else if (reference.kind === 'unsafe') {
            replacement = (options.renderUnsafe ?? defaultRenderUnsafe)(reference);
        }
        if (replacement === null)
            continue;
        output =
            output.slice(0, reference.index) +
                replacement +
                output.slice(reference.index + reference.markdown.length);
    }
    return output;
}
// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------
function defaultRenderUnsafe(reference) {
    return escapeMarkdownText(reference.alt);
}
/**
 * Swaps the destination inside one `![...](...)` occurrence, leaving every other
 * character where it was.
 *
 * Located by hand rather than by capture offsets, which keeps a pair of indices
 * only this function would ever read out of the public reference type. The alt
 * text cannot contain `]`, so the first `](` is always the delimiter.
 */
function replaceDestination(reference, destination) {
    const delimiter = reference.indexOf('](');
    if (delimiter === -1)
        return reference;
    let start = delimiter + 2;
    while (start < reference.length && (reference[start] === ' ' || reference[start] === '\t')) {
        start += 1;
    }
    let end = start;
    if (reference[start] === '<') {
        const close = reference.indexOf('>', start + 1);
        if (close === -1)
            return reference;
        end = close + 1;
    }
    else {
        // The same rule IMAGE_PATTERN uses for a bare destination, balanced
        // parentheses included. It has to be the same rule: a scan that stopped at
        // the first '(' would splice the new URL in front of the remains of the old
        // destination and produce nonsense.
        while (end < reference.length) {
            const character = reference[end];
            if (character === ' ' || character === '\t' || character === ')')
                break;
            if (character === '(') {
                const close = reference.indexOf(')', end + 1);
                if (close === -1)
                    break;
                end = close + 1;
                continue;
            }
            end += 1;
        }
    }
    return reference.slice(0, start) + destination + reference.slice(end);
}
/** True when the character at `index` is escaped by an odd run of backslashes. */
function isEscaped(text, index) {
    let backslashes = 0;
    for (let i = index - 1; i >= 0 && text[i] === '\\'; i -= 1)
        backslashes += 1;
    return backslashes % 2 === 1;
}
/** Undoes markdown backslash escapes, for a value that is meant to be displayed. */
function unescapeMarkdown(text) {
    return text.replace(/\\([\\`*_{}[\]()#+\-.!<>|~"'])/g, '$1');
}
/**
 * Escapes alt text for the inside of `![...]`: `]` would end it early,
 * backslashes have to be doubled to survive, and the emphasis characters are
 * escaped for the same reason `escapeMarkdownText` escapes them - alt text is
 * prose, and a stray asterisk in it should not become emphasis.
 */
function escapeAltText(alt) {
    return alt.replace(/([\\`*_[\]<>])/g, '\\$1').replace(/[\r\n]+/g, ' ');
}
function trimDecimal(value) {
    const rounded = value.toFixed(1);
    return rounded.endsWith('.0') ? rounded.slice(0, -2) : rounded;
}
function ascii(text) {
    return [...text].map((character) => character.charCodeAt(0));
}
function startsWith(bytes, signature) {
    return matchesAt(bytes, 0, signature);
}
function matchesAt(bytes, offset, signature) {
    if (bytes.byteLength < offset + signature.length)
        return false;
    for (let i = 0; i < signature.length; i += 1) {
        if (bytes[offset + i] !== signature[i])
            return false;
    }
    return true;
}
//# sourceMappingURL=articleImages.js.map